import {
    Box,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import SwitchToggle from "../../components/SwitchToggle";
import SearchBar from "../../components/SearchBar";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import { RoleResponse, useDeleteRoleByIdMutation, useLazyGetAllRolesQuery, useUpdateRoleByIdMutation } from "../../services/accessRole";
import { isValidInput } from "../../utils/validations";
import Pagination from "../../components/Panigation";
import DeleteConfirmationModal from "../../components/DeleteModal";
import { showError, showToast } from "../../helpers";
import usePermission from "../../hooks/userAccess";

const RolesList = () => {
    const navigate = useNavigate();

    const { isView, isAdd, isEdit, isDelete } = usePermission({
        label: 'Manage Sub-Admin',
        page_permission: 'isView',
    })

    const [searchTerm, setSearchTerm] = useState("");
    const [limit, setLimit] = useState(10);
    const [openDelete, setOpenDelete] = useState(false);
    const [selectedId, setSelectedId] = useState<string>("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
    const [roles, setRoles] = useState<RoleResponse[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [triggerGetAllRoles, { isFetching }] = useLazyGetAllRolesQuery();
    const [deleteRole] = useDeleteRoleByIdMutation();
    const [updateRole] = useUpdateRoleByIdMutation();

    const [page, setPage] = useState(1);

    const skip = (page - 1) * limit;
    const totalPages = Math.max(1, Math.ceil((totalCount || 0) / (limit || 10)));

    const onPageChange = (newPage: number) => {
        setPage(newPage);
    };

    const toggleDeleteModal = () => {
        setOpenDelete(!openDelete);
    };

    const handleDelete = async () => {
        try {
            const res = await deleteRole({ id: selectedId }).unwrap();
            if (res?.statusCode === 200) {
                showToast("Role deleted");
                toggleDeleteModal();
                fetchList();
            }
        } catch (error: any) {
            showError(error?.message || error?.data?.message || "Failed to delete role");
        }
    };

    const toggleStatus = async (id: string, currentStatus: boolean) => {
        try {
            const response = await updateRole({
                id,
                body: { isBlocked: !currentStatus },
            }).unwrap();

            if (response?.statusCode === 200) {
                showToast(
                    `Role ${!currentStatus ? " deactivated" : "activated"} successfully`
                );
                fetchList();
            } else {
                showError("Failed to update Role status");
            }
        } catch (error: any) {
            showError(error?.data?.message || "Failed to update Role status");
        }
    };

    const fetchList = async () => {
        try {
            const res = await triggerGetAllRoles({ skip, limit, search: debouncedSearchTerm }).unwrap();
            // Try to normalize various possible shapes
            const data = res?.data ?? res;
            const list = data?.SubAdminAccess ?? data?.list ?? data?.items ?? data?.rows ?? data;
            const count = data?.count ?? data?.total ?? (Array.isArray(list) ? list.length : 0);
            if (Array.isArray(list)) {
                setRoles(list as RoleResponse[]);
            } else {
                setRoles([]);
            }
            setTotalCount(count as number);
        } catch (e) {
            setRoles([]);
            setTotalCount(0);
        }
    };

    useEffect(() => {
        fetchList();
    }, [triggerGetAllRoles, limit, debouncedSearchTerm, skip]);

    return (
        <>
            <div className="table_header">
                <div className="left_s">
                    <Box className="search_box">
                        <SearchBar
                            searchTerm={searchTerm}
                            placeholder="Search..."
                            setDebouncedSearchTerm={setDebouncedSearchTerm}
                            value={searchTerm}
                            onCross={() => setSearchTerm('')}
                            onChange={(val: any) => {
                                if (isValidInput(val.target.value)) {
                                    setSearchTerm(val.target.value);
                                }
                            }}
                        />
                    </Box>
                </div>
                <div className="right_s">
                    {isAdd && <Button className="btn btn_primary" onClick={() => navigate("/manage-subadmin/roles/add")}><AddIcon /> Add Role</Button>}
                </div>
            </div>
            <TableContainer className="table_container">
                <Table>
                    <TableHead>
                        <TableRow hover>
                            <TableCell>Role Name</TableCell>
                            <TableCell>Created Date</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {roles.map((row) => (
                            <TableRow hover key={row?._id}>
                                <TableCell>
                                    <a
                                        href={isView ? `/manage-subadmin/roles/detail/${row._id}` : ""}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            if (isView) navigate(`/manage-subadmin/roles/detail/${row._id}`);
                                        }}
                                        className="user_block click_action"
                                    >
                                        <p>
                                            <b>{row?.name || '-'}</b>
                                        </p>
                                    </a>

                                </TableCell>
                                <TableCell>
                                    {row?.createdAt ? new Date(row.createdAt).toLocaleDateString() : "-"}
                                </TableCell>
                                <TableCell><SwitchToggle checked={!row?.isBlocked} onChange={(checked) => toggleStatus(row._id, checked)} /></TableCell>
                                <TableCell>
                                    <div className="table_actions v2">
                                        <Box>
                                            {isView && <IconButton
                                                onClick={() => navigate(`/manage-subadmin/roles/detail/${row._id}`)}
                                            >
                                                <img src="/static/images/eye_icon.svg" alt="view" />
                                            </IconButton>}
                                        </Box>
                                        <Box>
                                            {isEdit && <IconButton
                                                onClick={() => navigate(`/manage-subadmin/roles/edit/${row._id}`)}
                                            >
                                                <img src="/static/images/edit2_icon.svg" alt="edit" />
                                            </IconButton>}
                                        </Box>
                                        <Box>
                                            {isDelete && <IconButton onClick={() => {
                                                setSelectedId(row?._id || '');
                                                toggleDeleteModal();
                                            }}>
                                                <img src="/static/images/trash2_icon.svg" alt="delete" />
                                            </IconButton>}
                                        </Box>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <Pagination
                    module={roles || []}
                    page={page}
                    setPage={setPage}
                    limit={limit}
                    setLimit={setLimit}
                    onPageChange={onPageChange}
                    totalPages={totalPages}
                />
            </TableContainer>
            <DeleteConfirmationModal
                open={openDelete}
                onClose={toggleDeleteModal}
                handleDelete={handleDelete}
                header="Are you sure you want to delete this role?"
            />
        </>
    );
};

export default RolesList;
